import * as fs from "node:fs";
import * as path from "node:path";

export interface ArtifactStore {
  exists(p: string): boolean;
  readText(p: string): string;
  writeText(p: string, text: string): void;
  readJson<T = unknown>(p: string): T;
  writeJson(p: string, data: unknown): void;
  list(p: string, pattern?: string): string[];
  delete(p: string): void;
  deleteTree(p: string): void;
  copyToPath(src: string, dst: string): void;
}

export class WorkspaceLayout {
  readonly workspace: string;

  constructor(workspace: string) {
    this.workspace = workspace;
  }

  get sourceText(): string {
    return path.join(this.workspace, "source.txt");
  }

  get directDir(): string {
    return path.join(this.workspace, "draft", "scriptctl", "direct");
  }

  get directScript(): string {
    return path.join(this.directDir, "script.initial.json");
  }

  get validation(): string {
    return path.join(this.directDir, "validation.json");
  }

  get runState(): string {
    return path.join(this.directDir, "run_state.json");
  }

  get episodeResultsDir(): string {
    return path.join(this.directDir, "episode_results");
  }

  get batchResultsDir(): string {
    return path.join(this.directDir, "batch_results");
  }

  outputScript(outputDir: string): string {
    return path.join(outputDir, "script.json");
  }
}

function globToRegExp(pattern: string): RegExp {
  let re = "";
  for (const ch of pattern) {
    if (ch === "*") re += "[^/]*";
    else if (ch === "?") re += "[^/]";
    else if (".+^$()[]{}|\\".includes(ch)) re += "\\" + ch;
    else re += ch;
  }
  return new RegExp("^" + re + "$");
}

export class LocalArtifactStore implements ArtifactStore {
  exists(p: string): boolean {
    return fs.existsSync(p);
  }

  readText(p: string): string {
    return fs.readFileSync(p, "utf-8");
  }

  writeText(p: string, text: string): void {
    fs.mkdirSync(path.dirname(p), { recursive: true });
    fs.writeFileSync(p, text, "utf-8");
  }

  readJson<T = unknown>(p: string): T {
    return JSON.parse(fs.readFileSync(p, "utf-8")) as T;
  }

  writeJson(p: string, data: unknown): void {
    this.writeText(p, JSON.stringify(data, null, 2) + "\n");
  }

  list(p: string, pattern: string = "*"): string[] {
    if (!fs.existsSync(p)) return [];
    const re = globToRegExp(pattern);
    const entries = fs.readdirSync(p);
    const matched: string[] = [];
    for (const name of entries) {
      if (re.test(name)) matched.push(path.join(p, name));
    }
    matched.sort();
    return matched;
  }

  delete(p: string): void {
    if (fs.existsSync(p)) fs.unlinkSync(p);
  }

  deleteTree(p: string): void {
    if (fs.existsSync(p)) fs.rmSync(p, { recursive: true, force: true });
  }

  copyToPath(src: string, dst: string): void {
    fs.mkdirSync(path.dirname(dst), { recursive: true });
    fs.copyFileSync(src, dst);
  }
}

export const DEFAULT_STORE: ArtifactStore = new LocalArtifactStore();
